import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config';

function HomePage({ onNavigateToCalendar, onNavigateToLogin, onNavigateToEvent, isLoggedIn }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHomeFeed();
  }, []);

  const fetchHomeFeed = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/home-feed/?limit=20`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch home feed:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0) return `In ${diffDays} days`;
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const isUpcoming = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
          <h1 style={styles.title}>Event Feed</h1>
        </div>
        <div style={styles.loading}>Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
          <h1 style={styles.title}>Event Feed</h1>
        </div>
        <div style={styles.error}>Error: {error}</div>
        <button onClick={fetchHomeFeed} style={styles.retryButton}>Retry</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img src="/static/img/monk.svg" alt="Monk" style={styles.logo} />
          <div style={styles.logoLabel}>
            djedjuler
          </div>
        </div>
        <h1 style={styles.title}>Event Feed</h1>
        {isLoggedIn ? (
          <button onClick={onNavigateToCalendar} style={styles.calendarButton}>
            📅 Calendar
          </button>
        ) : (
          <button onClick={onNavigateToLogin} style={styles.loginButton}>
            🔐 Login
          </button>
        )}
      </div>

      {/* Feed */}
      <div style={styles.feed}>
        {events.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No events to show</p>
            {isLoggedIn ? (
              <button onClick={onNavigateToCalendar} style={styles.addEventButton}>
                Add Event
              </button>
            ) : (
              <p style={styles.emptySubtext}>Check back soon for upcoming events!</p>
            )}
          </div>
        ) : (
          events.map((event, index) => (
            <div key={`${event.date}-${index}`} style={styles.eventCard} onClick={() => onNavigateToEvent(event.date)}>
              {/* Event Header */}
              <div style={styles.eventHeader}>
                <div style={styles.eventInfo}>
                  <h2 style={styles.eventName}>{event.day_name}</h2>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {event.location?.name && (
                      <div style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}>
                        {event.location.name}
                      </div>
                    )}
                    <div style={{
                      ...styles.dateChip,
                      backgroundColor: isUpcoming(event.date) ? '#4CAF50' : '#9E9E9E'
                    }}>
                      {formatDate(event.date)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Poster Image */}
              {event.poster_url && (
                <div style={styles.posterContainer}>
                  <img 
                    src={event.poster_url} 
                    alt={`${event.day_name} poster`}
                    style={styles.poster}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Event Details */}
              <div style={styles.eventDetails}>
                <div style={styles.dateText}>
                  📅 {new Date(event.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>

                {/* DJ Lineup */}
                {event.slots && event.slots.length > 0 ? (
                  <div style={styles.djSection}>
                    <h3 style={styles.djHeader}>🎧 DJ Lineup</h3>
                    <div style={styles.djList}>
                      {event.slots.map((slot, slotIndex) => (
                        <div key={slotIndex} style={styles.djSlot}>
                          <div style={styles.djName}>{slot.dj || 'TBD'}</div>
                          <div style={styles.djTime}>
                            {slot.start_time} - {slot.end_time}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={styles.noDjs}>
                    <p>🎵 DJs coming soon...</p>
                  </div>
                )}
              </div>

              {/* Action Bar */}
              <div style={styles.actionBar}>
                <button style={styles.actionButton} onClick={async (e) => {
                  e.stopPropagation(); // Prevent event card click
                  
                  const eventUrl = `${window.location.origin}/event/${event.date}`;
                  const locationName = event.location?.name || 'Monk Amsterdam';
                  const shareData = {
                    title: `${event.day_name} at ${locationName}`,
                    text: `Join us for ${event.day_name} on ${formatDate(event.date)}!`,
                    url: eventUrl
                  };

                  // Mobile: Use native share API if available
                  if (navigator.share && /Mobile|Android|iPhone|iPad/.test(navigator.userAgent)) {
                    try {
                      await navigator.share(shareData);
                      return;
                    } catch (err) {
                      console.log('Native share failed, falling back to clipboard');
                    }
                  }

                  // Desktop: Copy to clipboard
                  try {
                    await navigator.clipboard.writeText(eventUrl);
                    // Show brief feedback
                    e.target.textContent = '✓ Copied!';
                    setTimeout(() => {
                      e.target.textContent = '📤 Share';
                    }, 2000);
                  } catch (err) {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = eventUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      e.target.textContent = '✓ Copied!';
                      setTimeout(() => {
                        e.target.textContent = '📤 Share';
                      }, 2000);
                    } catch (copyErr) {
                      alert(`Share this link: ${eventUrl}`);
                    }
                    document.body.removeChild(textArea);
                  }
                }}>
                  📤 Share
                </button>
                <button 
                  style={{
                    ...styles.actionButton,
                    ...(event.soundcloud_url ? styles.enabledButton : styles.disabledButton)
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event card click
                    if (event.soundcloud_url) {
                      window.open(event.soundcloud_url, '_blank');
                    }
                  }}
                  disabled={!event.soundcloud_url}
                >
                  🎵 {event.soundcloud_url ? 'Listen' : 'No Recording'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pull to refresh hint */}
      <div style={styles.refreshHint}>
        <button onClick={fetchHomeFeed} style={styles.refreshButton}>
          🔄 Refresh Feed
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    backgroundColor: '#fafafa',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif'
  },
  header: {
    backgroundColor: 'white',
    padding: '16px 20px',
    borderBottom: '1px solid #e1e1e1',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  logoContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    height: '32px',
    width: 'auto'
  },
  logoLabel: {
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%) rotate(-20deg)',
    backgroundColor: 'red',
    color: 'white',
    fontWeight: 'bold',
    padding: '1px 4px',
    fontSize: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap',
    borderRadius: '2px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#262626',
    flex: 1,
    textAlign: 'center'
  },
  calendarButton: {
    backgroundColor: '#0095f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  loginButton: {
    backgroundColor: '#42a5f5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  feed: {
    paddingBottom: '80px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#8e8e8e',
    fontSize: '16px'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: '#ed4956',
    fontSize: '16px'
  },
  retryButton: {
    display: 'block',
    margin: '0 auto',
    backgroundColor: '#0095f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    cursor: 'pointer'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#8e8e8e'
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#8e8e8e',
    fontStyle: 'italic',
    margin: '8px 0 0 0'
  },
  addEventButton: {
    backgroundColor: '#0095f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '16px'
  },
  eventCard: {
    backgroundColor: 'white',
    marginBottom: '20px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease',
    ':hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }
  },
  eventHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #f1f1f1'
  },
  eventInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px'
  },
  eventName: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: '#262626'
  },
  dateChip: {
    padding: '6px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  posterContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1/1',
    overflow: 'hidden'
  },
  poster: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  eventDetails: {
    padding: '16px 20px'
  },
  dateText: {
    fontSize: '14px',
    color: '#8e8e8e',
    marginBottom: '16px'
  },
  djSection: {
    marginTop: '16px'
  },
  djHeader: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#262626'
  },
  djList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  djSlot: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#f8f8f8',
    borderRadius: '8px'
  },
  djName: {
    fontWeight: '600',
    color: '#262626'
  },
  djTime: {
    fontSize: '14px',
    color: '#8e8e8e'
  },
  noDjs: {
    textAlign: 'center',
    padding: '20px',
    color: '#8e8e8e',
    fontStyle: 'italic'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '12px 0',
    borderTop: '1px solid #f1f1f1'
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#8e8e8e',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '8px 16px'
  },
  enabledButton: {
    color: '#0095f6',
    cursor: 'pointer'
  },
  disabledButton: {
    color: '#c7c7c7',
    cursor: 'not-allowed'
  },
  refreshHint: {
    textAlign: 'center',
    padding: '20px'
  },
  refreshButton: {
    backgroundColor: 'transparent',
    border: '1px solid #dbdbdb',
    color: '#8e8e8e',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    cursor: 'pointer'
  }
};

export default HomePage;